import React from 'react'
import { motion } from 'framer-motion'
import { Link, useNavigate } from 'react-router-dom'
import { 
  ArrowLeft,
  Settings,
  Eye,
  Gift
} from 'lucide-react'

const MomentPerksPage: React.FC = () => {
  const navigate = useNavigate()
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  }

  const itemVariants = {
    hidden: { y: 24, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94],
      },
    },
  }



  return (
    <div className="h-screen flex flex-col bg-[#F8F9FA] overflow-hidden no-scroll-page" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Fixed Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link
              to="/"
              className="inline-flex items-center space-x-2 text-[#6B7280] hover:text-[#1A2B49] transition-colors duration-200 font-medium"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Home</span>
            </Link>
            
            <div className="hidden sm:flex items-center space-x-1">
              <div className="w-2 h-2 bg-[#FE4215] rounded-full"></div>
              <div className="w-2 h-2 bg-[#E5E7EB] rounded-full"></div>
              <div className="w-2 h-2 bg-[#E5E7EB] rounded-full"></div>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content Container */}
      <div className="flex-1 pt-16 overflow-hidden">
        {/* Hero Section - Full Screen */}
        <section className="relative h-[calc(100vh-4rem)] flex items-center justify-center overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-r from-[#FE4215]/10 to-[#1A2B49]/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-gradient-to-r from-[#1A2B49]/10 to-[#FE4215]/10 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="text-center w-full scale-100 sm:scale-100 xs:scale-95 2xs:scale-90"
          >
            {/* Logo/Icon */}
            <motion.div variants={itemVariants} className="mb-6 sm:mb-8 md:mb-12">
              <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 bg-gradient-to-r from-[#FE4215] to-[#E53910] rounded-2xl shadow-lg">
                <Gift className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-white" />
              </div>
            </motion.div>
            
              {/* Main Heading */}
            <motion.h1
              variants={itemVariants}
              className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-bold text-[#1A2B49] mb-6 sm:mb-8 md:mb-12 tracking-tight"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Moment
              <span className="bg-gradient-to-r from-[#FE4215] to-[#E53910] bg-clip-text text-transparent">
                Perks
              </span>
            </motion.h1>
            
            {/* Subtitle */}
            <motion.p
              variants={itemVariants}
              className="text-xl sm:text-2xl lg:text-3xl text-[#6B7280] max-w-5xl mx-auto leading-relaxed mb-10 sm:mb-12"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Transform critical shopping moments into measurable revenue with
              <span className="font-semibold text-[#1A2B49]"> intelligent AI-driven offers </span> 
              that dramatically boost conversions and customer loyalty.
            </motion.p>

            {/* CTA Section */}
            <motion.div variants={itemVariants} className="mb-8 sm:mb-12">
              <h2 className="text-3xl sm:text-4xl font-semibold text-[#1A2B49] mb-6 sm:mb-10" style={{ fontFamily: 'Inter, sans-serif' }}>
                How do you want to see MomentPerks Integration?
              </h2>
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-8 max-w-3xl mx-auto">
                <button 
                  onClick={() => navigate('/moment-customization')}
                  className="group w-full sm:w-auto inline-flex items-center justify-center space-x-3 px-10 py-5 bg-gradient-to-r from-[#FE4215] to-[#E53910] text-white rounded-lg hover:from-[#E53910] hover:to-[#D73510] transition-all duration-300 font-semibold text-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                  style={{ 
                    fontFamily: 'Inter, sans-serif',
                    borderRadius: '8px'
                  }}
                >
                  <Settings className="w-7 h-7 group-hover:rotate-90 transition-transform duration-300" />
                  <span>Customize Moments</span>
                </button>
                
                <button 
                  onClick={() => navigate('/moments-gallery')}
                  className="group w-full sm:w-auto inline-flex items-center justify-center space-x-3 px-10 py-5 bg-[#1A2B49] text-white rounded-lg hover:bg-[#233D71] transition-all duration-300 font-semibold text-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                  style={{ 
                    fontFamily: 'Inter, sans-serif',
                    borderRadius: '8px'
                  }}
                >
                  <Eye className="w-7 h-7 group-hover:scale-110 transition-transform duration-300" />
                  <span>View Examples</span>
                </button>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
      </div>
    </div>
  )
}

export default MomentPerksPage
